import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Clock, Download, FileAudio, FileVideo, Loader2, StopCircle, Trash2, Wand2, X } from 'lucide-react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { Button } from './UIComponents';
import MediaDropzone from './MediaDropzone';

const FFMPEG_CORE_BASE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';

type OutputFormat = 'mp3' | 'wav' | 'ogg';

interface FormatConfig {
  extension: string;
  mimeType: string;
  codec: string[];
  label: string;
  description: string;
}

const FORMAT_CONFIGS: Record<OutputFormat, FormatConfig> = {
  mp3: {
    extension: 'mp3',
    mimeType: 'audio/mpeg',
    codec: ['-c:a', 'libmp3lame', '-b:a', '192k'],
    label: 'MP3',
    description: 'Best compatibility, good quality',
  },
  wav: {
    extension: 'wav',
    mimeType: 'audio/wav',
    codec: ['-c:a', 'pcm_s16le'],
    label: 'WAV',
    description: 'Lossless, larger file size',
  },
  ogg: {
    extension: 'ogg',
    mimeType: 'audio/ogg',
    codec: ['-c:a', 'libopus', '-b:a', '128k'],
    label: 'OGG (Opus)',
    description: 'Modern codec, excellent quality',
  },
};

interface ConvertedFile {
  id: string;
  name: string;
  originalName: string;
  size: number;
  url: string;
  duration: number;
  format: OutputFormat;
}

interface QueuedFile {
  id: string;
  file: File;
  status: 'pending' | 'converting' | 'completed' | 'error' | 'cancelled';
  progress: number;
  error?: string;
  result?: ConvertedFile;
}

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);
  return `${minutes}m ${remainingSeconds}s`;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const AudioConverter: React.FC = () => {
  const ffmpegRef = useRef(new FFmpeg());
  const abortRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('mp3');
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      queue.forEach(item => {
        if (item.result?.url) URL.revokeObjectURL(item.result.url);
      });
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowFormatDropdown(false);
    if (showFormatDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showFormatDropdown]);

  const loadFFmpeg = async () => {
    if (isReady) return true;
    if (isLoading) return false;
    setIsLoading(true);
    setError(null);

    try {
      if (typeof SharedArrayBuffer === 'undefined') {
        throw new Error('SharedArrayBuffer is not available. Cross-origin isolation headers may be missing.');
      }

      const ffmpeg = ffmpegRef.current;
      ffmpeg.on('log', ({ message }) => {
        console.log('[FFmpeg]', message);
      });

      const coreURL = await toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.js`, 'text/javascript');
      const wasmURL = await toBlobURL(`${FFMPEG_CORE_BASE_URL}/ffmpeg-core.wasm`, 'application/wasm');

      await ffmpeg.load({ coreURL, wasmURL });
      setIsReady(true);
      return true;
    } catch (loadError) {
      console.error('FFmpeg load error:', loadError);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load FFmpeg.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilesSelected = useCallback((files: File[]) => {
    const newItems: QueuedFile[] = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      file,
      status: 'pending',
      progress: 0,
    }));
    setQueue(prev => [...prev, ...newItems]);
    setError(null);
  }, []);

  const removeFromQueue = useCallback((id: string) => {
    setQueue(prev => {
      const item = prev.find(q => q.id === id);
      if (item?.result?.url) URL.revokeObjectURL(item.result.url);
      return prev.filter(q => q.id !== id);
    });
  }, []);

  const clearCompleted = useCallback(() => {
    setQueue(prev => {
      prev.forEach(item => {
        if (item.status === 'completed' && item.result?.url) {
          URL.revokeObjectURL(item.result.url);
        }
      });
      return prev.filter(q => q.status !== 'completed');
    });
  }, []);

  const convertFile = async (queuedFile: QueuedFile, format: OutputFormat): Promise<ConvertedFile | null> => {
    const { file, id } = queuedFile;
    const startTime = Date.now();
    const config = FORMAT_CONFIGS[format];

    const ffmpeg = ffmpegRef.current;
    const extension = file.name.split('.').pop() || 'media';
    const baseName = file.name.replace(/\.[^/.]+$/, '') || 'converted';
    const inputName = `input-${id}.${extension}`;
    const outputFileName = `${baseName}.${config.extension}`;

    const progressHandler = ({ progress }: { progress: number }) => {
      setQueue(prev => prev.map(q =>
        q.id === id ? { ...q, progress: Math.min(100, Math.round(progress * 100)) } : q
      ));
    };
    ffmpeg.on('progress', progressHandler);

    try {
      await ffmpeg.writeFile(inputName, await fetchFile(file));

      if (abortRef.current) {
        throw new Error('Conversion cancelled');
      }

      // Build FFmpeg command based on format
      const command = ['-i', inputName, ...config.codec, outputFileName];
      await ffmpeg.exec(command);

      if (abortRef.current) {
        throw new Error('Conversion cancelled');
      }

      const data = await ffmpeg.readFile(outputFileName);
      const uint8Data = typeof data === 'string' ? new TextEncoder().encode(data) : data;
      const audioBlob = new Blob([new Uint8Array(uint8Data)], { type: config.mimeType });
      const url = URL.createObjectURL(audioBlob);

      const duration = Date.now() - startTime;

      try {
        await ffmpeg.deleteFile(inputName);
        await ffmpeg.deleteFile(outputFileName);
      } catch {
        // Best-effort cleanup
      }

      return {
        id,
        name: outputFileName,
        originalName: file.name,
        size: audioBlob.size,
        url,
        duration,
        format,
      };
    } finally {
      ffmpeg.off('progress', progressHandler);
    }
  };

  const handleConvert = async () => {
    const pendingFiles = queue.filter(q => q.status === 'pending');
    if (pendingFiles.length === 0) return;

    abortRef.current = false;
    setIsConverting(true);
    setError(null);

    try {
      if (!isReady) {
        const ready = await loadFFmpeg();
        if (!ready) return;
      }

      for (const queuedFile of pendingFiles) {
        if (abortRef.current) break;

        setQueue(prev => prev.map(q =>
          q.id === queuedFile.id ? { ...q, status: 'converting', progress: 0 } : q
        ));

        try {
          const result = await convertFile(queuedFile, outputFormat);

          if (abortRef.current) {
            setQueue(prev => prev.map(q =>
              q.id === queuedFile.id ? { ...q, status: 'cancelled', progress: 0 } : q
            ));
          } else if (result) {
            setQueue(prev => prev.map(q =>
              q.id === queuedFile.id ? { ...q, status: 'completed', progress: 100, result } : q
            ));
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Conversion failed';
          if (errorMessage === 'Conversion cancelled') {
            setQueue(prev => prev.map(q =>
              q.id === queuedFile.id ? { ...q, status: 'cancelled', progress: 0 } : q
            ));
          } else {
            setQueue(prev => prev.map(q =>
              q.id === queuedFile.id ? { ...q, status: 'error', error: errorMessage } : q
            ));
          }
        }
      }
    } finally {
      setIsConverting(false);
      abortRef.current = false;
    }
  };

  const handleCancel = () => {
    abortRef.current = true;
    setQueue(prev => prev.map(q =>
      q.status === 'pending' ? { ...q, status: 'cancelled' } : q
    ));
  };

  const pendingCount = queue.filter(q => q.status === 'pending').length;
  const completedCount = queue.filter(q => q.status === 'completed').length;
  const hasQueue = queue.length > 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Upload Area */}
      <MediaDropzone
        embedded
        multiple
        title="Drop video or audio files"
        description="Supports MP4, MOV, AVI, MKV, WebM, MP3, WAV, M4A, and more."
        onFilesSelected={handleFilesSelected}
      />

      {/* Format Selector */}
      <div className="flex items-center justify-center gap-3">
        <span className="text-sm text-slate-600 dark:text-zinc-400">Output format:</span>
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowFormatDropdown(!showFormatDropdown);
            }}
            disabled={isConverting}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 rounded-lg text-sm font-medium text-slate-900 dark:text-zinc-100 hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
          >
            <span>{FORMAT_CONFIGS[outputFormat].label}</span>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </button>

          {showFormatDropdown && (
            <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden">
              {(Object.keys(FORMAT_CONFIGS) as OutputFormat[]).map(format => (
                <button
                  key={format}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOutputFormat(format);
                    setShowFormatDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors ${
                    outputFormat === format ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${outputFormat === format ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-900 dark:text-zinc-100'}`}>
                      {FORMAT_CONFIGS[format].label}
                    </span>
                    {outputFormat === format && (
                      <Check className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                    {FORMAT_CONFIGS[format].description}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Queue */}
      {hasQueue && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-700 dark:text-zinc-300">
              Files ({queue.length})
            </h3>
            {completedCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearCompleted}>
                Clear completed
              </Button>
            )}
          </div>

          <div className="space-y-2">
            {queue.map(item => (
              <div
                key={item.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                  item.status === 'completed'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                    : item.status === 'error'
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : item.status === 'cancelled'
                    ? 'bg-slate-100 dark:bg-zinc-800 border-slate-200 dark:border-zinc-700'
                    : 'bg-white dark:bg-zinc-800/50 border-slate-200 dark:border-zinc-700'
                }`}
              >
                {/* Icon */}
                <div className="h-10 w-10 bg-slate-100 dark:bg-zinc-700 rounded-lg flex items-center justify-center shrink-0">
                  {item.file.type.startsWith('video') ? (
                    <FileVideo className="text-indigo-600 dark:text-indigo-400 h-5 w-5" />
                  ) : (
                    <FileAudio className="text-indigo-600 dark:text-indigo-400 h-5 w-5" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 dark:text-zinc-100 truncate text-sm" title={item.file.name}>
                    {item.file.name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-400">
                    <span>{formatFileSize(item.file.size)}</span>
                    {item.status === 'converting' && (
                      <span className="text-indigo-600 dark:text-indigo-400">{item.progress}%</span>
                    )}
                    {item.status === 'completed' && item.result && (
                      <>
                        <span className="text-emerald-600 dark:text-emerald-400 uppercase font-medium">
                          {item.result.format}
                        </span>
                        <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <Clock className="h-3 w-3" />
                          {formatDuration(item.result.duration)}
                        </span>
                      </>
                    )}
                    {item.status === 'error' && (
                      <span className="text-red-600 dark:text-red-400">{item.error}</span>
                    )}
                    {item.status === 'cancelled' && (
                      <span className="text-slate-500 dark:text-zinc-500">Cancelled</span>
                    )}
                  </div>

                  {item.status === 'converting' && (
                    <div className="mt-1.5 h-1 bg-slate-200 dark:bg-zinc-600 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-600 rounded-full transition-all duration-300"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Status/Actions */}
                <div className="shrink-0 flex items-center gap-1">
                  {item.status === 'pending' && (
                    <button
                      onClick={() => removeFromQueue(item.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {item.status === 'converting' && (
                    <Loader2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400 animate-spin" />
                  )}
                  {item.status === 'completed' && item.result && (
                    <>
                      <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      <a
                        href={item.result.url}
                        download={item.result.name}
                        className="p-1.5 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                      </a>
                    </>
                  )}
                  {(item.status === 'error' || item.status === 'cancelled') && (
                    <button
                      onClick={() => removeFromQueue(item.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col items-center gap-4">
        {isConverting ? (
          <Button
            onClick={handleCancel}
            variant="secondary"
            className="px-8"
            size="lg"
          >
            <StopCircle className="mr-2 h-5 w-5" />
            Cancel Conversion
          </Button>
        ) : (
          <Button
            onClick={handleConvert}
            disabled={pendingCount === 0 || isLoading}
            className="px-8"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading FFmpeg...
              </>
            ) : (
              <>
                <Wand2 className="mr-2 h-5 w-5" />
                Convert {pendingCount > 0 ? `${pendingCount} file${pendingCount > 1 ? 's' : ''}` : ''} to {FORMAT_CONFIGS[outputFormat].label}
              </>
            )}
          </Button>
        )}

        {isLoading && (
          <div className="w-full max-w-xs">
            <div className="h-2 bg-slate-200 dark:bg-zinc-700 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-600 rounded-full animate-pulse w-1/2" />
            </div>
            <p className="text-center text-sm text-slate-500 dark:text-zinc-400 mt-2">
              Loading FFmpeg engine...
            </p>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-600 dark:text-red-400 text-center">
          {error}
        </div>
      )}

      {/* Completed Results with Audio Preview */}
      {completedCount > 0 && (
        <div className="bg-white dark:bg-zinc-800/50 rounded-xl border border-slate-200 dark:border-zinc-700 p-6 space-y-4">
          <h3 className="font-medium text-slate-900 dark:text-zinc-100 text-center">
            Converted Files ({completedCount})
          </h3>
          <div className="space-y-4">
            {queue
              .filter(q => q.status === 'completed' && q.result)
              .map(item => (
                <div key={item.id} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-zinc-700 rounded text-xs font-medium text-slate-600 dark:text-zinc-300 uppercase">
                        {item.result!.format}
                      </span>
                      <span className="font-medium text-slate-700 dark:text-zinc-300 truncate" title={item.result!.name}>
                        {item.result!.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400 shrink-0 ml-2">
                      <span>{formatFileSize(item.result!.size)}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDuration(item.result!.duration)}
                      </span>
                    </div>
                  </div>
                  <audio controls className="w-full h-10">
                    <source src={item.result!.url} type={FORMAT_CONFIGS[item.result!.format].mimeType} />
                  </audio>
                </div>
              ))}
          </div>

          {completedCount > 1 && (
            <div className="pt-2 border-t border-slate-100 dark:border-zinc-700">
              <p className="text-center text-xs text-slate-500 dark:text-zinc-400">
                Click the download icon next to each file to save individually
              </p>
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <p className="text-center text-xs text-slate-400 dark:text-zinc-500">
        All processing happens in your browser. Your files never leave your device.
      </p>
    </div>
  );
};

export default AudioConverter;
